import { useBlockProps, InspectorControls, BlockControls } from "@wordpress/block-editor";
import { PanelBody, SelectControl, TextControl, TextareaControl, Button, ButtonGroup, ToggleControl, Disabled, ToolbarGroup, ToolbarDropdownMenu } from "@wordpress/components";
import { useEffect, useRef } from "@wordpress/element";
import { useSelect } from "@wordpress/data";
import DebouncedServerSideRender from "../../../shared/js/DebouncedServerSideRender";
import { __, sprintf } from "@wordpress/i18n";
import { isDuplicateTocAnchor } from "../../../shared/js/tocAnchorDiagnostics";
import ProductRefControl from "../../../shared/js/ProductRefControl";
import useCeggEditorProducts from "../../../shared/js/useCeggEditorProducts";

const VARIANT_OPTIONS = [
    { label: __("Default", "content-egg"), value: "default" },
    { label: __("Featured", "content-egg"), value: "featured" },
    { label: __("Compact", "content-egg"), value: "compact" },
];

const COLOR_SCHEME_OPTIONS = [
    { label: __("Auto (site default)", "content-egg"), value: "auto" },
    { label: __("Light", "content-egg"), value: "light" },
    { label: __("Dark", "content-egg"), value: "dark" },
];

export default function Edit({ attributes, setAttributes }) {
    const blockProps = useBlockProps({ className: attributes.align ? `align${attributes.align}` : "" });
    const productSnapshot = useCeggEditorProducts();
    const {
        variant,
        product_ref,
        section_label,
        block_title,
        heading_tag,
        title,
        rank,
        badge,
        score,
        subtitle,
        chips = [],
        description,
        merchant,
        cta_label,
        anchor,
        toc_label,
        include_in_toc,
        level,
        color_scheme,
        seeded_for,
        enable_schema,
    } = attributes;
    const allBlocks = useSelect((select) => select("core/block-editor").getBlocks(), []);
    const hasDuplicateTocAnchor = !!include_in_toc && isDuplicateTocAnchor(allBlocks, anchor);
    const suggestedTocLabel = (block_title || section_label || "").trim();

    const lastPrefilledProductKeyRef = useRef("");
    const lastSeededValuesRef = useRef({
        title: "",
        subtitle: "",
        merchant: "",
        badge: "",
    });
    const selectedProduct = (productSnapshot.all || []).find((product) => (
        String(product.module_id || "") === String(product_ref?.module_id || "") &&
        String(product.unique_id || "") === String(product_ref?.unique_id || "")
    )) || null;
    const hasConfiguredProductRef = !!(product_ref?.module_id && product_ref?.unique_id);

    const updateChips = (nextChips) => setAttributes({ chips: nextChips });
    const addChip = () => updateChips([...(chips || []), ""]);
    const updateChip = (index, value) => {
        const next = [...(chips || [])];
        next[index] = value;
        updateChips(next);
    };
    const removeChip = (index) => {
        const next = [...(chips || [])];
        next.splice(index, 1);
        updateChips(next);
    };

    useEffect(() => {
        const productKey = `${String(product_ref?.module_id || "")}:${String(product_ref?.unique_id || "")}`;

        if (!selectedProduct || productKey === ":") {
            lastPrefilledProductKeyRef.current = "";
            lastSeededValuesRef.current = {
                title: "",
                subtitle: "",
                merchant: "",
                badge: "",
            };
            return;
        }

        if (lastPrefilledProductKeyRef.current === productKey) {
            return;
        }

        if (String(seeded_for || "") === productKey) {
            lastPrefilledProductKeyRef.current = productKey;
            lastSeededValuesRef.current = {
                title: String(selectedProduct.title || "").trim(),
                subtitle: String(selectedProduct.subtitle || "").trim(),
                merchant: String(selectedProduct.merchant || selectedProduct.domain || "").trim(),
                badge: String(selectedProduct.badge || "").trim(),
            };
            return;
        }

        const nextSeededValues = {
            title: String(selectedProduct.title || "").trim(),
            subtitle: String(selectedProduct.subtitle || "").trim(),
            merchant: String(selectedProduct.merchant || selectedProduct.domain || "").trim(),
            badge: String(selectedProduct.badge || "").trim(),
        };
        const previousSeededValues = lastSeededValuesRef.current;
        const nextAttributes = {};

        const currentTitle = (title || "").trim();
        if (nextSeededValues.title !== "" && (currentTitle === "" || currentTitle === previousSeededValues.title)) {
            nextAttributes.title = nextSeededValues.title;
        }

        const currentSubtitle = (subtitle || "").trim();
        if (nextSeededValues.subtitle !== "" && (currentSubtitle === "" || currentSubtitle === previousSeededValues.subtitle)) {
            nextAttributes.subtitle = nextSeededValues.subtitle;
        }

        const currentMerchant = (merchant || "").trim();
        if (nextSeededValues.merchant !== "" && (currentMerchant === "" || currentMerchant === previousSeededValues.merchant)) {
            nextAttributes.merchant = nextSeededValues.merchant;
        }

        const currentBadge = (badge || "").trim();
        if (nextSeededValues.badge !== "" && (currentBadge === "" || currentBadge === previousSeededValues.badge)) {
            nextAttributes.badge = nextSeededValues.badge;
        }

        lastPrefilledProductKeyRef.current = productKey;
        lastSeededValuesRef.current = nextSeededValues;
        nextAttributes.seeded_for = productKey;

        setAttributes(nextAttributes);
    }, [badge, merchant, product_ref?.module_id, product_ref?.unique_id, seeded_for, selectedProduct, setAttributes, subtitle, title]);

    const hasAuthoredContent = !!(
        (title || "").trim()
        || (description || "").trim()
        || (subtitle || "").trim()
        || (Array.isArray(chips) && chips.some((chip) => (chip || "").trim() !== ""))
    );
    const hasPreviewContent = !!(selectedProduct || hasConfiguredProductRef || hasAuthoredContent);

    return (
        <>
            <BlockControls>
                <ToolbarGroup>
                    <ToolbarDropdownMenu
                        icon="screenoptions"
                        label={__("Variant", "content-egg")}
                        text={VARIANT_OPTIONS.find((o) => o.value === variant)?.label}
                        controls={VARIANT_OPTIONS.map((option) => ({
                            title: option.label,
                            isActive: variant === option.value,
                            onClick: () => setAttributes({ variant: option.value }),
                        }))}
                    />
                </ToolbarGroup>
            </BlockControls>

            <InspectorControls>
                <PanelBody title={__("Layout", "content-egg")} initialOpen={true}>
                    <SelectControl
                        label={__("Variant", "content-egg")}
                        value={variant}
                        options={VARIANT_OPTIONS}
                        onChange={(value) => setAttributes({ variant: value })}
                    />
                    <SelectControl
                        label={__("Color Scheme", "content-egg")}
                        value={color_scheme}
                        options={COLOR_SCHEME_OPTIONS}
                        onChange={(value) => setAttributes({ color_scheme: value })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                <PanelBody title={__("Product Binding", "content-egg")} initialOpen={!hasConfiguredProductRef}>
                    <ProductRefControl
                        value={product_ref}
                        onChange={(value) => setAttributes({ product_ref: value, seeded_for: "" })}
                    />
                    <div style={{ marginTop: "16px" }}>
                        <ToggleControl
                            label={__("Enable product schema (JSON-LD)", "content-egg")}
                            checked={!!enable_schema}
                            onChange={(value) => setAttributes({ enable_schema: value })}
                            __nextHasNoMarginBottom
                        />
                    </div>
                </PanelBody>

                <PanelBody title={__("Header", "content-egg")} initialOpen={false}>
                    <TextControl
                        label={__("Section Label", "content-egg")}
                        value={section_label}
                        onChange={(value) => setAttributes({ section_label: value })}
                    />
                    <TextControl
                        label={__("Title", "content-egg")}
                        value={block_title}
                        onChange={(value) => setAttributes({ block_title: value })}
                    />
                    <div style={{ marginBottom: "8px" }}>
                        <div style={{ fontSize: "11px", fontWeight: 500, marginBottom: "6px" }}>
                            {__("Heading Tag", "content-egg")}
                        </div>
                        <ButtonGroup>
                            {["h1", "h2", "h3", "h4", "div"].map((tag) => (
                                <Button
                                    key={tag}
                                    variant={heading_tag === tag ? "primary" : "secondary"}
                                    size="small"
                                    onClick={() => setAttributes({ heading_tag: tag })}
                                >
                                    {tag.toUpperCase()}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </div>
                </PanelBody>

                <PanelBody title={__("Content", "content-egg")} initialOpen={true}>
                    <TextControl
                        label={__("Title", "content-egg")}
                        value={title}
                        onChange={(value) => setAttributes({ title: value })}
                    />
                    <TextControl
                        label={__("Rank", "content-egg")}
                        value={rank}
                        onChange={(value) => setAttributes({ rank: value })}
                    />
                    <TextControl
                        label={__("Badge", "content-egg")}
                        value={badge}
                        onChange={(value) => setAttributes({ badge: value })}
                    />
                    <TextControl
                        label={__("Score", "content-egg")}
                        value={score}
                        onChange={(value) => setAttributes({ score: value })}
                    />
                    <TextareaControl
                        label={__("Subtitle", "content-egg")}
                        value={subtitle}
                        onChange={(value) => setAttributes({ subtitle: value })}
                        rows={2}
                    />
                    <TextareaControl
                        label={__("Description", "content-egg")}
                        value={description}
                        onChange={(value) => setAttributes({ description: value })}
                        rows={3}
                    />
                </PanelBody>

                <PanelBody title={__("Merchant & CTA", "content-egg")} initialOpen={false}>
                    <TextControl
                        label={__("Merchant", "content-egg")}
                        value={merchant}
                        onChange={(value) => setAttributes({ merchant: value })}
                    />
                    <TextControl
                        label={__("CTA Label", "content-egg")}
                        value={cta_label}
                        onChange={(value) => setAttributes({ cta_label: value })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                <PanelBody title={__("Chips", "content-egg")} initialOpen={false}>
                    {(chips || []).map((chip, index) => (
                        <div key={index} style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "6px" }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <TextControl
                                    value={chip}
                                    onChange={(value) => updateChip(index, value)}
                                    __nextHasNoMarginBottom
                                />
                            </div>
                            <Button
                                icon="no-alt"
                                isDestructive
                                size="small"
                                onClick={() => removeChip(index)}
                                label={__("Remove", "content-egg")}
                                style={{ flexShrink: 0 }}
                            />
                        </div>
                    ))}
                    <Button variant="secondary" size="small" onClick={addChip} style={{ marginTop: "4px" }}>
                        {__("Add chip", "content-egg")}
                    </Button>
                </PanelBody>

                <PanelBody title={__("TOC", "content-egg")} initialOpen={false}>
                    <TextControl
                        label={__("Anchor", "content-egg")}
                        value={anchor || ""}
                        onChange={(value) => setAttributes({ anchor: value })}
                    />
                    {hasDuplicateTocAnchor && (
                        <p style={{ marginTop: "-4px", marginBottom: "12px", color: "#8a1f11", fontSize: "12px" }}>
                            {__("This anchor is already used by another TOC block.", "content-egg")}
                        </p>
                    )}
                    <ToggleControl
                        label={__("Include In TOC", "content-egg")}
                        checked={!!include_in_toc}
                        onChange={(value) => setAttributes({ include_in_toc: value })}
                    />
                    {include_in_toc && !(anchor || "").trim() && (
                        <p style={{ marginTop: "-4px", marginBottom: "12px", color: "#757575", fontSize: "12px" }}>
                            {__("Set an anchor so the TOC can link to this block.", "content-egg")}
                        </p>
                    )}
                    <TextControl
                        label={__("TOC Label", "content-egg")}
                        value={toc_label || ""}
                        onChange={(value) => setAttributes({ toc_label: value })}
                    />
                    {include_in_toc && !(toc_label || "").trim() && (
                        <p style={{ marginTop: "-4px", marginBottom: "12px", color: "#757575", fontSize: "12px" }}>
                            {suggestedTocLabel
                                ? sprintf(__("Suggested label: %s", "content-egg"), `"${suggestedTocLabel}"`)
                                : __("Set a TOC label so this block can appear in automatic TOC mode.", "content-egg")}
                        </p>
                    )}
                    <TextControl
                        label={__("Level", "content-egg")}
                        type="number"
                        min={1}
                        max={6}
                        value={level ?? 2}
                        onChange={(value) => setAttributes({ level: value === "" ? 2 : Number(value) })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>
            </InspectorControls>

            <div {...blockProps}>
                {hasPreviewContent ? (
                    <Disabled>
                        <DebouncedServerSideRender
                            block="eggb/product-card"
                            attributes={attributes}
                        />
                    </Disabled>
                ) : (
                    <p style={{ color: "#999", fontStyle: "italic", padding: "16px", margin: 0, fontSize: "13px" }}>
                        {__("Bind a Content Egg product to preview this block.", "content-egg")}
                    </p>
                )}
            </div>
        </>
    );
}
